import * as React from "react";
import {Fragment} from "react";
import {DndProvider} from "react-dnd";
import HTML5Backend from "react-dnd-html5-backend";
import {Checkbox, Col, Divider, Form, Icon, Input, InputNumber, Row, Select, TimePicker, Tooltip, Switch} from "antd";
import {Image} from "@models/image";
import ImagePicker from "@components/custom/imagePicker";
import {createRequest, fetchItemRequest, updateRequest} from "@actions/video-content";
import {
  Video,
  VideoContentFlag,
  VideoContentFlagOptions,
  VideoContentStatus,
  VideoContentStatusOptions,
  VideoContentType,
  VideoContentTypeOptions,
  VideoContentAgeRestrictionType,
  VideoContentAgeRestrictionTypeOptions
} from "@models/video-content";
import TextEditor, {TextEditorRef} from "@components/custom/TextEditor";
import {FormattedMessage} from "react-intl";
import {SelectionMode} from "@models/index";
import {
  categoryMultiSelectionDefaultReturnValue,
  CategoryMultiSelectionReturnValue
} from "@components/categories/Selection";
import TagsSelection, {
  tagMultiSelectionDefaultReturnValue,
  TagMultiSelectionReturnValue
} from "@components/tags/Selection";
import SuppliersSelection from "@components/supplier/Selection";
import PackagesSelection from "@components/packages/Selection";
import PersonsRoles, {PersonsRolesRef} from "@components/video-contents/PersonsRoles";
import Gallery from "@components/video-contents/galleries/Gallery";
import moment from "moment-jalaali";
import DatePicker from "react-datepicker2";
import {VideoContentSortOrder, VideoContentSortOrderOptions} from "@models/video-content";
import PersonSelection from "@components/people/Selection";
import {Radio} from "antd";
import VideoSelection, {
  videoMultiSelectionDefaultReturnValue,
  VideoMultiSelectionReturnValue
} from "@components/video-contents/Selection";
import {Person} from "@models/person";
import {REGEX_PATTERNS} from "@constants/regex";
import VideoCategorySelection from "@components/video-contents/VideoCategorySelection";
import withFormWrapper from "@hocs/withFormWrapper";
import {toUTC, showDate} from "@utils";
import {Package} from '@models/package';
import FilesManager from "./files/FilesManager";

//TO DO orientation="right" ?  orientation="left"

const Option = Select.Option;

const enum VideoPackageType {
  "CONTENT_LIST_ITEMS" = "content_list_items",
  "CONTENT_LIST_STRATEGY" = "content_list_strategy"
}

interface PropsFromState {
  loading: boolean;
  errors?: string;
  form: any;
  videoContent?: Video;
  className?: string;
  history?: any;
}

interface PropsFromDispatch {
  createRequest: typeof createRequest;
  updateRequest: typeof updateRequest;
  fetchItemRequest: typeof fetchItemRequest;
}

type VideoFormAllProps = PropsFromState & PropsFromDispatch;

type VideoFormStateShape = Video & {
  cover_image: Image | null;
  poster_image: Image | null;
  logo_image: Image | null;
};

/**
 * VideoContent form is so complex and has a lot of details
 * Type is disabled in edit mode : it would cause a lot of problem
 */
class VideoForm extends React.Component<VideoFormAllProps,
  VideoFormStateShape> {
  private categories: CategoryMultiSelectionReturnValue = this.props.videoContent?.id ? categoryMultiSelectionDefaultReturnValue : 'no id!';
  private tags: TagMultiSelectionReturnValue = this.props.videoContent?.id ? tagMultiSelectionDefaultReturnValue : 'no id!';
  private videos: VideoMultiSelectionReturnValue = videoMultiSelectionDefaultReturnValue;
  // private files: VideoFile = videoMultiSelectionDefaultReturnValue;


  categorySelectionRef = React.createRef();
  supplierSelectionRef = React.createRef();
  packageSelectionRef = React.createRef();
  galleryRef = React.createRef();
  filesManagerRef = React.createRef();

  constructor(props) {

    super(props);
    const {videoContent} = props;
    this.state = {
      ...this.getStateFromProps(videoContent)
    };
    if (this.tags === 'no id!' && videoContent?.tags?.length) {
      this.tags = {...tagMultiSelectionDefaultReturnValue, new_tags: videoContent.tags.map(({ id }) => id)}
    }
    if (this.categories === 'no id!' && videoContent?.categories?.length) {
      this.categories = {...categoryMultiSelectionDefaultReturnValue, new_categories: videoContent.categories.map(({ id }) => id)}
    }
  }

  getStateFromProps = (video: Video) => {
    //TODO check just once and set default values instead of inline if for each property!
    return {
      video_file_id: null,
      price: video ? video.price : null,
      share: video ? video.share : null,
      total_count: video && video.strategy ? video.strategy.total_count : null,
      type: video ? video.type : VideoContentType.UNSPECIFIED,
      visible: false,
      title: video ? video.title : "",
      page_title: video?.page_title ,
      original_name: video ? video.original_name : "",
      supper_title: video ? video.supper_title : "",
      upper_title: video ? video.upper_title : "",
      sub_title: video ? video.sub_title : "",
      slug: video ? video.slug : "",
      season: video && video.season,
      episode: video && video.episode,
      summary: video ? video.summary : "",
      poster_image: video ? video.poster_image : null,
      cover_image: video ? video.cover_image : null,
      logo_image: video ? video.logo_image : null,
      flag: video ? video.flag.split(",").map(flag => flag.trim()).join(",") : VideoContentFlag.None, //Because the value from server is comma and space seperated
      categories: video && video.categories ? video.categories : [],
      tags: video && video.tags ? video.tags : [],
      supplier: video ? video.supplier : null,
      package: video ? video.package : null,
      video_content_person_roles: video && video.video_content_person_roles ? video.video_content_person_roles : [],
      id: video ? video.id : null,
      is_fetched: video ? video.is_fetched : false,
      published_at: (video && video.published_at != "0001-01-01T00:00:00") ? moment(showDate(video.published_at)) : null,
      duration: video ? video.duration : "",
      imdb_rank_percent: video ? video.imdb_rank * 10 : null,
      year: video && video.year ? parseInt(video.year) : "",
      imdb_url: video ? video.imdb_url : null,
      status: video ? video.status : VideoContentStatus.ACTIVE,
      contentListType: video && video.content_list_items ? VideoPackageType.CONTENT_LIST_ITEMS : VideoPackageType.CONTENT_LIST_STRATEGY,
      content_list_items: video && video.content_list_items ? video.content_list_items.filter(item => !!item) : [],
      related_video_content: video && video.related_video_content ? video.related_video_content : null,
      related_video_content_parent: video ? video.related_video_content2 : null,
      age_restriction: video && video.age_restriction ? video.age_restriction : VideoContentAgeRestrictionType.NOTSET,
      iran_access: video?.iran_access ?? false,
      coming_soon: (video?.coming_soon && video.coming_soon != "0001-01-01T00:00:00") ? moment(showDate(video.coming_soon)) : null,
    };
  };

  private textEditorRef = React.createRef<TextEditorRef>();
  private personRoleSelectionRef = React.createRef<PersonsRolesRef>();
  // private galleryRef = React.createRef<GalleryRef>();

  /**
   * called from FormModal HOC
   * returns all the values should be passed in payload
   */
  getValues = () => {
    let returnValues = null;
    //this is a trick to validate values those are not a form input
    this.props.form.setFieldsValue({
      "cover_image_id": this.state.cover_image ? this.state.cover_image.id : "",
      "logo_image_id": this.state.logo_image ? this.state.logo_image.id : "",
      "poster_image_id": this.state.poster_image ? this.state.poster_image.id : "",
      "summary": this.textEditorRef.current ? this.textEditorRef.current.value : "",
      "duration": this.state.duration ? this.state.duration : null,
      "supplier_id": this.state.supplier ? this.state.supplier.id : null,
      "published_at": this.state.published_at ? new Date(this.state.published_at).toISOString() : null, // new Date(this.state.published_at.format("YYYY-MM-DD")).toISOString() : null,
      "coming_soon": this.state.coming_soon ? new Date(this.state.coming_soon).toISOString() : null,
      "related_video_content_id": this.state.related_video_content ? this.state.related_video_content.id : null,
      //To preventing get validation error in edit mode
      "content_list_items": this.state.type == VideoContentType.CONTENT_LIST && (this.videos && this.videos.new_videos.length > 0 || this.state.content_list_items.length > 0) ? (this.videos && this.videos.new_videos.length > 0 ? this.videos.new_videos : this.state.content_list_items) : null,
      "content_list_strategy.tag_ids": this.state.type == VideoContentType.CONTENT_LIST && this.tags.new_tags.length > 0 ? this.tags.new_tags : null,
      "content_list_strategy.category_ids":
        this.state.type == VideoContentType.CONTENT_LIST &&
        this.categories.new_categories.length > 0 ? this.categories.new_categories : null,
      "content_list_strategy.related_person_id": this.state.type == VideoContentType.CONTENT_LIST && this.state.person ? this.state.person.id : null
    });

    this.props.form.validateFieldsAndScroll((err, values) => {
      if (!err) {
        returnValues = {
          videoContent: {
            ...values,
            cover_image_id: this.state.cover_image ? this.state.cover_image.id : null,
            logo_image_id: this.state.logo_image ? this.state.logo_image.id : null,
            flag: this.state.flag || VideoContentFlag.None,
            rate: 2.5
          }
        };

        if (this.state.type == VideoContentType.CONTENT_LIST) {
          //To exclude content_list_items and content_list_strategy from video content
          const {
            content_list_strategy,
            content_list_items,
            ...rest
          } = returnValues.videoContent;
          if (
            this.state.contentListType == VideoPackageType.CONTENT_LIST_STRATEGY
          ) {
            returnValues = {
              videoContent: rest,
              content_list_strategy
            };
          } else {
            returnValues = {
              videoContent: rest,
              content_list_items: this.videos
            };
          }
        }

        returnValues = {
          ...returnValues,
          categories: this.categories,
          tags: this.tags,
          personRole: this.personRoleSelectionRef.current ? this.personRoleSelectionRef.current.value : {},
          gallery: this.galleryRef.current ? this.galleryRef.current.value : null,
          files: this.filesManagerRef.current ? this.filesManagerRef.current.value : null
        };
      } else {
        console.log(err);
      }
    });

    return {
      payload: returnValues,
      id: this.props.videoContent ? this.props.videoContent.id : null
    };
  };

  onChangeCategories = (categories: CategoryMultiSelectionReturnValue) => {
    this.categories = categories;
  };

  onChangeTags = (tags: TagMultiSelectionReturnValue) => {
    this.tags = tags;
  };

  onChangeSuppliers = supplier => {
    this.setState({
      supplier: supplier
    });
  };

  onChangePackage = (item: Package) => {
    this.setState({
      "package": item
    });
  };

  onChangeVideoFile = item => {
    // this.setState({
    //   video_file_id: item.id
    // });
  };

  onChangeVideos = (videos: VideoMultiSelectionReturnValue) => {
    this.videos = videos;
  };

  setCoverImage = (image: Image) => {
    this.setState({
      cover_image: image
    });
  };

  setLogoImage = (image: Image) => {
    this.setState({
      logo_image: image
    });
  };

  setPosterImage = (image: Image) => {
    this.setState({
      poster_image: image
    });
  };

  onChangeFlags = checkedValues => {
    this.setState({
      flag: checkedValues.join(",")
    });
  };

  onChangeVideoContentType = type => {
    this.setState({
      type,
      related_video_content: null,
      related_video_content_parent: null
    });
  };

  onChangeVideoContentAgeRestrictionType = age_restriction => {
    this.setState({
      age_restriction
    });
  };

  onChangeRelatedVideo = relatedVideo => {
    this.setState({
      related_video_content: relatedVideo
    });

    this.props.form.setFieldsValue({
      "upper_title": relatedVideo.title
    })
  };

  onChangeRelatedVideoParent = relatedVideo => {
    this.setState({
      related_video_content: null,
      related_video_content_parent: relatedVideo
    });

    this.props.form.setFieldsValue({
      "supper_title": relatedVideo.title
    })
  }

  onChangeStatus = status => {
    this.setState({status});
  };

  onChangeDate = (type, date) => {
    this.setState({
      [type]: date
    })
  }

  onChangeDuration = value => {
    this.setState({
      duration: value.format("HH:mm:ss")
    });
  };

  onChangeListType = e => {
    this.setState({
      contentListType: e.target.value
    });
  };

  onChangePerson = (person: Person): void => {
    this.setState({
      person
    });
  };

  getFlagOptions = () => {
    const isDownlaodable = this.state.type === VideoContentType.EPISOD || this.state.type === VideoContentType.SINGLE;
    return isDownlaodable ?
      [...VideoContentFlagOptions, {
        label: <FormattedMessage id="app.downloadable" defaultMessage={`Downloadable`}/>,
        value: VideoContentFlag.Downloadable
      }] :
      VideoContentFlagOptions
  };

  render() {
    const {getFieldDecorator} = this.props.form;
    const relatedVideo = VideoContentTypeOptions.find(
      item => item.value == this.state.type
    )["relatedVideoContentType"];
    const relatedVideoParent =
      relatedVideo &&
      VideoContentTypeOptions.find(item => item.value == relatedVideo)[
        "relatedVideoContentType"
        ];
        
     let params = new URLSearchParams(this.props.history?.location.search);

    return (
      <Form className={"video-form"}>
        <Row>
          <Col span={6}>
            <Form.Item className={"no-label video-logo-wrapper"}>
              {getFieldDecorator("logo_image_id")(
                <ImagePicker
                  onSelectImage={this.setLogoImage}
                  selectedImage={this.state.logo_image}
                >
                  <div
                    className={`image-wrapper`}
                    style={{
                      margin: "auto",
                      backgroundImage: `url(${
                        this.state.logo_image
                          ? this.state.logo_image.path
                          : ""
                      })`
                    }}
                  >
                    <FormattedMessage
                      id="app.logo_image"
                      defaultMessage={`logo Image`}
                    />
                  </div>
                </ImagePicker>
              )}
            </Form.Item>
            <Form.Item className={"no-label video-poster-wrapper"}>
              {getFieldDecorator("poster_image_id", {
                rules: [{
                  // whitespace: true,
                  required: true,
                  message: (
                    <FormattedMessage
                      id="app.poster_image_is_required"
                      defaultMessage={`Poster image is required!`}
                    />
                  )
                }]
              })(
                <ImagePicker onSelectImage={this.setPosterImage} selectedImage={this.state.poster_image}>
                  <div
                    className={`image-wrapper`}
                    style={{
                      backgroundImage: `url(${
                        this.state.poster_image
                          ? this.state.poster_image.path
                          : ""
                      })`
                    }}
                  >
                    <FormattedMessage
                      id="app.select_poster_image"
                      defaultMessage={`Select poster image`}
                    />
                  </div>
                </ImagePicker>
              )}
            </Form.Item>
          </Col>
          <Col span={18}>
            <Form.Item className={"no-label video-cover-wrapper"}>
              {getFieldDecorator("cover_image_id", {
                rules: [{
                  required: true,
                  message: (
                    <FormattedMessage
                      id="app.cover_image_is_required"
                      defaultMessage={`Cover image is required!`}
                    />
                  )
                }]
              })(
                <ImagePicker
                  onSelectImage={this.setCoverImage}
                  selectedImage={this.state.cover_image}
                >
                  <div
                    className={`image-wrapper`}
                    style={{
                      backgroundImage: `url(${
                        this.state.cover_image
                          ? this.state.cover_image.path
                          : ""
                      })`
                    }}
                  >
                    <FormattedMessage
                      id="app.cover_image"
                      defaultMessage={`Cover Image`}
                    />
                  </div>
                </ImagePicker>
              )}
            </Form.Item>
          </Col>

        </Row>

        <Row gutter={24}>
          {/*TYPE*/}
          <Col span={8}>
            <Form.Item label={<FormattedMessage id="app.type" defaultMessage={`Type`}/>}>
              {getFieldDecorator("type", {
                initialValue: this.state.type
              })(
                <Select
                  onChange={this.onChangeVideoContentType}
                  disabled={this.state.id ? true : false}
                >
                  {VideoContentTypeOptions.map((opt, index) => (
                    <Option key={index} value={opt.value}>
                      {opt.label}
                    </Option>
                  ))}
                </Select>
              )}
            </Form.Item>
          </Col>

          {relatedVideo && (
            <Fragment>
              {relatedVideoParent && (
                <>
                  <Col span={8}>
                    <Form.Item
                      label={
                        VideoContentTypeOptions.find(
                          item => item.value == relatedVideoParent
                        ).label
                      }
                    >
                      {getFieldDecorator("related_video_content_parent_id", {
                        initialValue: this.state.related_video_content_parent
                          ? this.state.related_video_content_parent.id
                          : null,
                        rules: [
                          {
                            required: true,
                            message: (
                              <FormattedMessage
                                id="app.related_video_is_required"
                                defaultMessage={`Select related video!`}
                              />
                            )
                          }
                        ]
                      })(
                        <Fragment>
                          <Input type={`hidden`}/>
                        </Fragment>
                      )}
                      <VideoSelection
                        key={`related_video_parent_${this.state.type}`}
                        filter={{
                          video_content_type: relatedVideoParent,
                          status: VideoContentStatus.ACTIVE
                        }}
                        mode={SelectionMode.DEFAULT}
                        onChange={this.onChangeRelatedVideoParent}
                        initialValue={this.state.related_video_content_parent}
                      />
                    </Form.Item>
                  </Col>
                </>
              )}

              {(this.state.type === VideoContentType.EPISOD || this.state.type === VideoContentType.SEASON) && (
                <Col span={8}>
                  <Form.Item label={
                    <FormattedMessage
                      id="app.supper_title"
                      defaultMessage={`Super Title`}
                    />
                  }>
                    {getFieldDecorator("supper_title", {
                      initialValue: this.state.supper_title
                    })(<Input disabled={true}/>)}
                  </Form.Item>
                </Col>
              )}

              <Col span={8}>
                <Form.Item label={VideoContentTypeOptions.find(item => item.value == relatedVideo).label}>
                  {getFieldDecorator("related_video_content_id", {
                    initialValue: this.state.related_video_content ? this.state.related_video_content.id : null,
                    rules: [{
                      required: true,
                      message: (
                        <FormattedMessage
                          id="app.related_video_is_required"
                          defaultMessage={`Select related video!`}
                        />
                      )
                    }]
                  })(
                    <Fragment>
                      <Input type={`hidden`}/>
                      <VideoSelection
                        key={`related_video_${this.state.type}`}
                        disabled={relatedVideoParent && !this.state.related_video_content_parent ? true : false}
                        filter={{
                          video_content_type: relatedVideo,
                          status: VideoContentStatus.ACTIVE,
                          ...(relatedVideoParent &&
                          this.state.related_video_content_parent ? {related_video_content_id: this.state.related_video_content_parent.id} : {})
                        }}
                        mode={SelectionMode.DEFAULT}
                        onChange={this.onChangeRelatedVideo}
                        initialValue={this.state.related_video_content}
                      />
                    </Fragment>
                  )}
                </Form.Item>
              </Col>
            </Fragment>
          )}

          {this.state.type === VideoContentType.EPISOD && (
            <Col span={8}>
              <Form.Item
                label={
                  <FormattedMessage
                    id="app.upper_title"
                    defaultMessage={`Upper Title`}
                  />
                }
              >
                {getFieldDecorator("upper_title", {
                  initialValue: this.state.upper_title
                })(<Input disabled={true}/>)}
              </Form.Item>
            </Col>
          )}

          {(this.state.type === VideoContentType.SEASON || this.state.type === VideoContentType.EPISOD) && (
            <Col span={8}>
              <Form.Item label={<FormattedMessage id="app.season_number" defaultMessage={`Season Number`}/>}>
                {getFieldDecorator("season", {
                  initialValue: this.state.season,
                  rules: [{
                    required: true,
                    message: (
                      <FormattedMessage
                        id="app.season_number_is_required"
                        defaultMessage={`Season number is required!`}
                      />
                    )
                  }]
                })(<InputNumber min={1} max={100}/>)}
              </Form.Item>
            </Col>
          )}

          {this.state.type === VideoContentType.EPISOD && (
            <Col span={8}>
              <Form.Item label={<FormattedMessage id="app.episode_number" defaultMessage={`Episode Number`}/>}>
                {getFieldDecorator("episode", {
                  initialValue: this.state.episode,
                  rules: [{
                    required: true,
                    message: (
                      <FormattedMessage
                        id="app.episode_number_is_required"
                        defaultMessage={`Episode number is required!`}
                      />
                    )
                  }]
                })(<InputNumber/>)}
              </Form.Item>
            </Col>
          )}

          <Col span={8}>
            <Form.Item label={<FormattedMessage id="app.title" defaultMessage={`Title`}/>}>
              {getFieldDecorator("title", {
                initialValue: this.state.title,
                rules: [{
                  whitespace: true,
                  required: true,
                  message: (
                    <FormattedMessage
                      id="app.title_is_required"
                      defaultMessage={`Title is required!`}
                    />
                  )
                }]
              })(<Input/>)}
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item label={<FormattedMessage id="app.original_name" defaultMessage={`Original Name`}/>}>
              {getFieldDecorator("original_name", {
                initialValue: this.state.original_name,
              })(<Input/>)}
            </Form.Item>
          </Col>


          {this.state.type === VideoContentType.EPISOD && (
            <>
              <Col span={8}>
                <Form.Item
                  label={
                    <FormattedMessage
                      id="app.sub_title"
                      defaultMessage={`Sub Title`}
                    />
                  }
                >
                  {getFieldDecorator("sub_title", {
                    initialValue: this.state.sub_title
                  })(<Input/>)}
                </Form.Item>
              </Col>
            </>
          )}


          <Col span={8}>
            <Form.Item
              label={<FormattedMessage id="app.slug" defaultMessage={`Slug`}/>}
            >
              {getFieldDecorator("slug", {
                initialValue: this.state.slug,
                rules: [{
                  whitespace: true,
                  required: true,
                  message: (
                    <FormattedMessage
                      id="app.slug_is_required"
                      defaultMessage={`Slug is required!`}
                    />
                  )
                }]
              })(<Input/>)}
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item label={<FormattedMessage id="page_title" defaultMessage={`Page Title`}/>}>
              {getFieldDecorator("page_title", {
                initialValue: this.state.page_title,
              })(<Input/>)}
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label={
                <FormattedMessage
                  id="app.age_restriction"
                  defaultMessage={`Age Restriction`}
                />
              }
            >
              {getFieldDecorator("age_restriction", {
                initialValue: this.state.age_restriction
              })(
                <Select onChange={this.onChangeVideoContentAgeRestrictionType}>
                  {VideoContentAgeRestrictionTypeOptions.map((opt, index) => (
                    <Option key={index} value={opt.value}>
                      {opt.label}
                    </Option>
                  ))}
                </Select>
              )}
            </Form.Item>
          </Col>

        </Row>
        <Row>
          <Col span={24}>
            <Form.Item label={<FormattedMessage id="app.description" defaultMessage={`Description`}/>}>
              {getFieldDecorator("summary", {
                initialValue: this.state.summary,
                rules: [{
                  whitespace: true,
                  required: true,
                  message: (
                    <FormattedMessage
                      id="app.summary_is_required"
                      defaultMessage={`Summary is required!`}
                    />
                  )
                }]
              })(
                <TextEditor
                  type="string"
                  ref={this.textEditorRef}
                  text={this.state.summary}
                />
              )}
            </Form.Item>
          </Col>
        </Row>

        <Fragment>
          <Divider dashed={true} orientation="left">
            <FormattedMessage id="app.details" defaultMessage={`Details`}/>
          </Divider>
          <Row gutter={24}>
            <Col span={8}>
              <Form.Item label={<FormattedMessage id="app.year" defaultMessage={`Year`}/>}>
                {getFieldDecorator("year", {
                  initialValue: this.state.year,
                  rules: [{
                    pattern: /^\d{4}$/,
                    message: (
                      <FormattedMessage
                        id="app.value_is_not_valid"
                        defaultMessage={`The value is not valid!`}
                      />
                    )
                  }]
                })(<Input/>)}
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label={<FormattedMessage id="app.status" defaultMessage={`Status`}/>}>
                {getFieldDecorator("status", {
                  initialValue: this.state.status
                })(
                  <Select onChange={this.onChangeStatus}>
                    {VideoContentStatusOptions.map((opt, index) => (
                      <Option key={index} value={opt.value}>
                        {opt.label}
                      </Option>
                    ))}
                  </Select>
                )}
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label={
                  <FormattedMessage
                    id="app.publish_date"
                    defaultMessage={`Published at`}
                  />
                }
              >
                {getFieldDecorator("published_at", {
                  initialValue: moment(this.state.published_at),
                  rules: [
                    {
                      // whitespace: true,
                      required: true,
                      message: (
                        <FormattedMessage
                          id="app.published_date_is_required"
                          defaultMessage={`Published date is required!`}
                        />
                      )
                    }
                  ]
                })(<Input type={`hidden`}/>)}
                <DatePicker
                  isGregorian={false}
                  timePicker={true}
                  value={this.state.published_at}
                  onChange={(date) => this.onChangeDate('published_at', date)}
                />{" "}
              </Form.Item>
            </Col>
          </Row>

          {this.state.type !== VideoContentType.CONTENT_LIST && (
            <Row gutter={24}>

              {/*Duration*/}
              {this.state.type !== VideoContentType.SERIES && this.state.type !== VideoContentType.SEASON && this.state.type !== VideoContentType.CONTENT_LIST && (
                <Col span={8}>
                  <Form.Item label={<FormattedMessage id="app.duration" defaultMessage={`Duration`}/>}>
                    {getFieldDecorator("duration", {
                      rules: [{
                        // whitespace: true,
                        required: true,
                        message: (
                          <FormattedMessage
                            id="app.duration_is_required"
                            defaultMessage={`Duration is required!`}
                          />
                        )
                      }]
                    })(<Input type={"hidden"}/>)}
                    <TimePicker
                      placeholder={""}
                      value={moment(this.state.duration, "HH:mm:ss")}
                      onChange={this.onChangeDuration}
                    />
                  </Form.Item>
                </Col>
              )}

              <Col span={8}>
                <Form.Item label={<FormattedMessage id="app.imdb_rank" defaultMessage={`IMDB Rank`}/>}>
                  {getFieldDecorator("imdb_rank_percent", {
                    initialValue: this.state.imdb_rank_percent
                  })(<InputNumber min={1} max={100}/>)}
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item label={<FormattedMessage id="app.imdb_url" defaultMessage={`IMDB Url`}/>}>
                  {getFieldDecorator("imdb_url", {
                    initialValue: this.state.imdb_url,
                    rules: [{
                      type: "url",
                      message: (
                        <FormattedMessage
                          id="app.invalid_url"
                          defaultMessage={`URL is invalid!`}
                        />
                      )
                    }]
                  })(<Input/>)}
                </Form.Item>
              </Col>
            </Row>
          )}

          <Row gutter={8}>
            <Col span={8}>
              <Form.Item className={'align-left'} label={<FormattedMessage id="iran_access" defaultMessage={`Iran Access`}/>}>
                {getFieldDecorator("iran_access", {
                  initialValue: this.state.iran_access
                })(
                  <Switch defaultChecked={this.state.iran_access}/>
                )}
              </Form.Item>
            </Col>
          </Row>

          <Row>
            <Col span={8}>
              <Form.Item
                label={
                  <FormattedMessage
                    id="app.coming_soon.date"
                  />
                }
              >
                {getFieldDecorator("coming_soon", {
                  initialValue: moment(this.state.coming_soon)
                })(<Input type={`hidden`}/>)}
                <DatePicker
                  isGregorian={false}
                  timePicker={true}
                  value={this.state.coming_soon}
                  onChange={(date) => this.onChangeDate('coming_soon', date)}
                />{" "}
              </Form.Item>
            </Col>
          </Row>

          {this.state.type !== VideoContentType.SERIES && this.state.type !== VideoContentType.SEASON && (
            <Fragment>
              <Divider dashed orientation="left">
                <FormattedMessage id="app.price" defaultMessage={`Price`}/>
              </Divider>
              <Row gutter={24}>
                <Col span={8} className={`right-align`}>
                  <Form.Item label={<FormattedMessage id="app.package" defaultMessage={`Package`}/>}>
                    {getFieldDecorator("package_id", {
                      initialValue: this.state.package && this.state.package.id
                    })}
                    <PackagesSelection
                      mode={SelectionMode.DEFAULT}
                      onChange={this.onChangePackage}
                      initialValue={this.state.package || null}
                    />
                  </Form.Item>
                </Col>

                <Col span={8}>
                  <Form.Item label={<FormattedMessage id="app.price" defaultMessage={`Price`}/>}>
                    {getFieldDecorator("price", {
                      initialValue: this.state.price
                    })(
                      <InputNumber
                        disabled={this.state.package && this.state.package.flag.indexOf("float_price") !== -1 ? false : true}/>
                    )}
                  </Form.Item>
                </Col>
              </Row>
            </Fragment>
          )}


          {/*Supplier*/}
          {this.state.type !== VideoContentType.SEASON && this.state.type !== VideoContentType.SERIES && (
            <Fragment>
              <Divider dashed orientation="left">
                <FormattedMessage id="app.supplier" defaultMessage={`Supplier`}/>
              </Divider>

              <Row gutter={24}>
                <Col span={8} className={`right-align`}>
                  <Form.Item
                    label={
                      <FormattedMessage
                        id="app.supplier"
                        defaultMessage={`Supplier`}
                      />
                    }
                  >
                    {getFieldDecorator("supplier_id", {
                      initialValue: this.state.supplier && this.state.supplier.id
                    })}
                    <SuppliersSelection
                      mode={SelectionMode.DEFAULT}
                      onChange={this.onChangeSuppliers}
                      ref={this.supplierSelectionRef}
                      filterOption={true}
                      initialValue={this.state.supplier || null}
                    />
                  </Form.Item>
                </Col>

                <Col span={8}>
                  <Form.Item label={<FormattedMessage id="app.share" defaultMessage={`Share`}/>}>
                    {getFieldDecorator("share", {
                      initialValue: this.state.share
                    })(
                      <Input disabled={this.state.supplier && this.state.supplier.id ? false : true}/>
                    )}
                  </Form.Item>
                </Col>
              </Row>
            </Fragment>
          )}

          <Divider dashed={true} orientation="left">
            <FormattedMessage id="app.flags" defaultMessage={`Flags`}/>
          </Divider>
          <Row className={`video-status-selection-wrapper`}>
            <Col span={24} className={`right-align`}>
              <Checkbox.Group
                options={this.getFlagOptions()}
                value={this.state.flag.split(",")}
                onChange={this.onChangeFlags}
              />
            </Col>
          </Row>

          <Divider dashed orientation="left">
            <FormattedMessage
              id="app.full_cast_crew"
              defaultMessage={`Full Cast & Crew`}
            />
          </Divider>
          <Row className={`video-person-role-list`}>
            <Col span={24} className={`right-align`}>
              <Form.Item>
                {getFieldDecorator(
                  "video_content_person_roles",
                  {}
                )(<Input type={`hidden`}/>)}
                <PersonsRoles
                  video_content_person_roles={
                    this.state.video_content_person_roles
                  }
                  ref={this.personRoleSelectionRef}
                />
              </Form.Item>
            </Col>
          </Row>
        </Fragment>

        {this.state.type == VideoContentType.CONTENT_LIST && (
          <Fragment>
            <Divider dashed={true} orientation="left"></Divider>
            <div className="ant-row ant-form-item">
              <div className="ant-col ant-form-item-label">
                <label>
                  <span>
                    <FormattedMessage
                      id="app.package_type"
                      defaultMessage={`Package Type`}
                    />
                  </span>
                </label>
              </div>
              <div className="ant-col ant-form-item-control-wrapper">
                <Radio.Group
                  onChange={this.onChangeListType}
                  value={this.state.contentListType}
                >
                  <Radio value={VideoPackageType.CONTENT_LIST_STRATEGY}>
                    <FormattedMessage
                      id="app.set_sterategy"
                      defaultMessage={`Set Strategy`}
                    />
                  </Radio>
                  <Radio value={VideoPackageType.CONTENT_LIST_ITEMS}>
                    <FormattedMessage
                      id="app.select_movies"
                      defaultMessage={`Select Movies`}
                    />
                  </Radio>
                </Radio.Group>
              </div>
            </div>

            {(this.state.contentListType ==
              VideoPackageType.CONTENT_LIST_STRATEGY && (
                <Fragment>
                  <Row gutter={24}>
                    <Col span={12}>
                      <Form.Item
                        label={
                          <FormattedMessage
                            id="app.year"
                            defaultMessage={`Year`}
                          />
                        }
                      >
                        {getFieldDecorator("content_list_strategy.year", {
                          rules: [
                            {
                              pattern: REGEX_PATTERNS.YEAR,
                              message: (
                                <FormattedMessage
                                  id="app.value_is_not_valid"
                                  defaultMessage={`The value is not valid!`}
                                />
                              )
                            }
                          ]
                        })(<Input/>)}
                      </Form.Item>
                    </Col>

                    <Col span={12}>
                      <Form.Item
                        label={
                          <FormattedMessage
                            id="app.related_person"
                            defaultMessage={`Relaed Person`}
                          />
                        }
                      >
                        {getFieldDecorator(
                          "content_list_strategy.related_person_id",
                          {
                            // initialValue : this.state.person ,
                          }
                        )(
                          <PersonSelection
                            mode={SelectionMode.DEFAULT}
                            onChange={this.onChangePerson}
                          />
                        )}
                      </Form.Item>
                    </Col>

                    <Col span={12}>
                      <Form.Item
                        label={
                          <FormattedMessage
                            id="app.sort_strategy"
                            defaultMessage={`Sort Strategy`}
                          />
                        }
                      >
                        {getFieldDecorator(
                          "content_list_strategy.sort_strategy",
                          {
                            initialValue: VideoContentSortOrder.LATEST
                          }
                        )(
                          <Select>
                            {VideoContentSortOrderOptions.map((opt, index) => (
                              <Option key={index} value={opt.value}>
                                {opt.label}
                              </Option>
                            ))}
                          </Select>
                        )}
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item
                        label={
                          <FormattedMessage
                            id="app.total_count"
                            defaultMessage={`Total Count`}
                          />
                        }
                      >
                        {getFieldDecorator("content_list_strategy.total_count", {
                          initialValue: this.state.total_count,
                          rules: [
                            {
                              required: true,
                              message: (
                                <FormattedMessage
                                  id="app.total_count_is_required"
                                  defaultMessage={`Total count is required!`}
                                />
                              )
                            }
                          ]
                        })(<InputNumber min={1} max={20}/>)}
                      </Form.Item>
                    </Col>
                  </Row>
                </Fragment>
              )) || (
              <Row>
                <Col span={24}>
                  <Form.Item label={<FormattedMessage id="app.movies" defaultMessage={`Movies`}/>}>
                    {getFieldDecorator("content_list_items", {
                      rules: [
                        {
                          // whitespace: true,
                          required: true,
                          message: (
                            <FormattedMessage
                              id="app.one_video_should_be_select_at_least"
                              defaultMessage={`One video should be select at least!`}
                            />
                          )
                        }]
                    })(
                      <VideoSelection
                        mode={SelectionMode.MULTIPLE}
                        filter={{
                          video_content_type: VideoContentType.SINGLE,
                          status: VideoContentStatus.ACTIVE
                        }}
                        onChange={this.onChangeVideos}
                        initialValue={this.state.content_list_items || []}
                      />
                    )}
                  </Form.Item>
                </Col>
              </Row>
            )}
          </Fragment>
        )}

        <Divider dashed orientation="left">
          <FormattedMessage id="app.tags" defaultMessage={`Tags`}/>
        </Divider>
        <Row>
          <Col span={24}>
            <Form.Item label={<FormattedMessage id="app.select_tag" defaultMessage={`Select Tag`}/>}>
              {getFieldDecorator("tags", {})}
              <TagsSelection
                mode={SelectionMode.MULTIPLE}
                onChange={this.onChangeTags}
                initialValue={this.state.tags || []}
                isDuplicatedForm={!!params.get('ref')}
              />
            </Form.Item>
          </Col>
        </Row>
        <Divider dashed orientation="left">
          <FormattedMessage id="app.categories" defaultMessage={`Categories`}/>
        </Divider>
        <Row>
          <Col span={24}>
            <Form.Item>
              {getFieldDecorator("categories", {})(<Input type={`hidden`}/>)}
            </Form.Item>
            <VideoCategorySelection
              ref={this.categorySelectionRef}
              form={this.props.form && this.props.form}
              onChange={this.onChangeCategories}
              selectedCategories={this.state.categories || []}
              isDuplicatedForm={!!params.get('ref')}
            />
          </Col>
        </Row>


        {/*SELECT FILES*/}

        <Divider dashed orientation="left">
          <FormattedMessage id="app.select_files" defaultMessage={`Select Files`}/>
        </Divider>
        <div>
          <FilesManager video_content_id={this.state.id}
                        ref={this.filesManagerRef}
                        />
        </div>

        <Fragment>
          <Divider dashed orientation="left">
            <FormattedMessage id="app.gallery" defaultMessage={`Gallery`}/>
          </Divider>

          {/*// TODO: dnd should be handled in the related class file :/ */}
          <Row gutter={24} className={`video-gallery-list`}>
            <Col span={24} className={`right-align`}>
              <Form.Item>
                {getFieldDecorator(
                  "video_content_gallery",
                  {}
                )(<Input type={`hidden`}/>)}
                <DndProvider backend={HTML5Backend}>
                  <Gallery
                    video_content_id={this.state.id}
                    ref={this.galleryRef}
                  />
                </DndProvider>
              </Form.Item>
            </Col>
          </Row>
        </Fragment>
      </Form>
    )
  }
}

const WrappedVideoForm = Form.create({name: "video-form"})(VideoForm);
export default withFormWrapper(WrappedVideoForm);
